import { LocationVarInheritance } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, Tooltip } from "@swivl-mono/ui";
import { ReactNode } from "react";
import { BiEditAlt } from "react-icons/bi";
import { GrRevert } from "react-icons/gr";

import { CortexInheritancePill } from "../../Views/Properties/CortexV3/Components/CortexInheritancePill";

interface SettingsCardProps {
  title: string;
  caption?: string;
  isEditing: boolean;
  saving: boolean;
  canSave?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => Promise<void>;
  children: ReactNode;
}

/** Bordered card shell with an Edit/Cancel-Save header. Shared by Details v3 settings pages. */
export function SettingsCard({
  title,
  caption,
  isEditing,
  saving,
  canSave = true,
  onEdit,
  onCancel,
  onSave,
  children
}: SettingsCardProps): React.ReactElement {
  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <h3 className="heading-base-alt text-grey-900">{title}</h3>
          {caption && <p className="body-sm mt-0.5 text-grey-600">{caption}</p>}
        </div>
        {isEditing ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={saving || !canSave} onClickAsync={onSave}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
          >
            Edit
            <BiEditAlt className="size-4" />
          </button>
        )}
      </div>
      <div className="border-t border-grey-200">{children}</div>
    </section>
  );
}

interface SettingRowProps {
  label: string;
  description?: string;
  /** Omit when the setting has no org/group/location cascade (e.g. a single org-only var). */
  inheritance?: LocationVarInheritance;
  isEditing: boolean;
  revert?: { label: string; revertsTo: string; onRevert: () => void };
  children: ReactNode;
}

/** One label/value row inside a SettingsCard. */
export function SettingRow({ label, description, inheritance, isEditing, revert, children }: SettingRowProps): React.ReactElement {
  return (
    <div className="mx-4 grid grid-cols-[200px_minmax(0,1fr)_auto] items-center gap-x-4 border-b border-grey-200 py-3 last:border-b-0">
      <span className="body-sm-alt text-grey-900">{label}</span>

      <div className="min-w-0">{children}</div>

      <div className="flex shrink-0 items-center gap-2">
        {inheritance !== undefined && <CortexInheritancePill inheritance={inheritance} />}
        {isEditing && revert && (
          <Tooltip content={`Reverts to: ${revert.revertsTo}`}>
            <button
              type="button"
              onClick={revert.onRevert}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              {revert.label}
              <GrRevert className="size-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {isEditing && description && <p className="body-sm col-start-2 mt-1 text-grey-600">{description}</p>}
    </div>
  );
}
